import { Paper, Stack } from '@mui/material';
import { Children, isValidElement, ReactNode } from 'react';
import { ROW_1_HEIGHT } from '@/components/organisms/PageHeader/constants';
import { PageHeaderActions } from '@/components/organisms/PageHeader/PageHeaderActions';
import { PageHeaderDescription } from '@/components/organisms/PageHeader/PageHeaderDescription';
import { PageHeaderFilters } from '@/components/organisms/PageHeader/PageHeaderFilters';
import { PageHeaderMeta } from '@/components/organisms/PageHeader/PageHeaderMeta';
import { PageHeaderMetaItem } from '@/components/organisms/PageHeader/PageHeaderMetaItem';
import { PageHeaderTitle } from '@/components/organisms/PageHeader/PageHeaderTitle';

type PageHeaderProps = {
  /**
   * Compound sub-components: `PageHeader.Title`, `PageHeader.Actions`,
   * `PageHeader.Filters`, `PageHeader.Meta`. Order does not matter — the
   * shell places each sub-component in the correct row and alignment. See
   * ADR-001 and ADR-002.
   */
  children: ReactNode;
};

type ChildSlots = {
  title: ReactNode;
  filters: ReactNode;
  actions: ReactNode;
  meta: ReactNode;
  description: ReactNode;
};

const partitionChildren = (children: ReactNode): ChildSlots => {
  const slots: ChildSlots = { title: null, filters: null, actions: null, meta: null, description: null };

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === PageHeaderTitle) slots.title = child;
    else if (child.type === PageHeaderFilters) slots.filters = child;
    else if (child.type === PageHeaderActions) slots.actions = child;
    else if (child.type === PageHeaderMeta) slots.meta = child;
    else if (child.type === PageHeaderDescription) slots.description = child;
  });

  return slots;
};

const PageHeaderRoot = ({ children }: PageHeaderProps) => {
  const { title, filters, actions, meta, description } = partitionChildren(children);
  const isCard = Boolean(meta) || Boolean(actions);

  return (
    <Paper
      variant="outlined"
      sx={{
        bgcolor: isCard ? 'background.neutral' : 'transparent',
        borderColor: isCard ? 'divider' : 'transparent',
        borderRadius: 1.5,
        px: isCard ? 2.5 : 0,
        py: isCard ? 2 : 0,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ height: ROW_1_HEIGHT }}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
          {title}
          {filters}
        </Stack>
        {actions}
      </Stack>
      {meta}
      {description}
    </Paper>
  );
};

/**
 * `PageHeader` is the unified header card for entity detail views (Building,
 * Portfolio, Economic Unit, Scenario, Action Plan).
 *
 * Compose it via its sub-components:
 *
 * ```tsx
 * <PageHeader>
 *   <PageHeader.Title statusBadge={<DraftBadge />} tooltip="Atelierstraße 29">
 *     <BuildingSelector />
 *   </PageHeader.Title>
 *   <PageHeader.Filters>
 *     <BuildingsSelector />
 *   </PageHeader.Filters>
 *   <PageHeader.Actions>
 *     <PIconButton icon={<DatabaseIcon />} aria-label="Data collection" />
 *     <PIconButton icon={<DownloadIcon />} aria-label="Export" />
 *   </PageHeader.Actions>
 *   <PageHeader.Meta isLoading={isLoading}>
 *     <PageHeader.MetaItem type="typeOfUse" label="Type of use" value="Gemischt I" />
 *     <PageHeader.MetaItem type="address" label="Address" value="Atelierstraße 29" />
 *     <PageHeader.MetaItem type="floorArea" label="Floor area" value="2,300 m²" />
 *   </PageHeader.Meta>
 *   <PageHeader.Description label="Description">
 *     Free-text building description, hidden behind a collapsible toggle.
 *   </PageHeader.Description>
 * </PageHeader>
 * ```
 *
 * See `DECISIONS.md` in this folder for the full set of ADRs that shaped
 * the API.
 */
export const PageHeader = Object.assign(PageHeaderRoot, {
  Title: PageHeaderTitle,
  Actions: PageHeaderActions,
  Filters: PageHeaderFilters,
  Meta: PageHeaderMeta,
  MetaItem: PageHeaderMetaItem,
  Description: PageHeaderDescription,
});
